import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCourseDetail } from '../features/course/hooks/useCourseDetail';
import { CourseSkeleton } from '../features/course/components/CourseSkeleton';
import { CourseHeader } from '../features/course/components/CourseHeader';
import { CourseSidebar } from '../features/course/components/CourseSidebar';
import { CourseTabs } from '../features/course/components/CourseTabs';
import { CourseCurriculum } from '../features/course/components/CourseCurriculum';
import CustomVideoPlayer from '../components/CustomVideoPlayer';
import { useToast } from '../components/Toast';

export const CourseDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showToast } = useToast();

  const {
    course, chapters, loading,
    selectedChapter, selectedVideo,
    courseProgress, videoProgressMap, videoTasksMap, chapterVideosMap, chapterTasksCountMap,
    setSelectedVideo, handleMarkComplete,
    isChapterUnlocked, isVideoUnlocked, isVideoCompleted,
    handleVideoTimeUpdate, slideLinks, imageLinks, submittingTask
  } = useCourseDetail(id);

  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo(0, 0);
  }, [id]);

  if (loading) {
    return <CourseSkeleton />;
  }

  if (!course) {
    return (
      <div className="container mx-auto py-16 text-center">
        <h2 className="text-2xl font-bold text-gray-900">{t('course.notFound')}</h2>
        <button
          onClick={() => navigate('/classes')}
          className="mt-4 text-indigo-600 hover:underline"
        >
          {t('menu.classes')}
        </button>
      </div>
    );
  }

  const handleEnroll = () => {
    // Navigate to payment or handle enroll logic
    navigate('/payment');
  };

  const handleContinue = () => {
    const videoSection = document.getElementById('video-player-section');
    if (videoSection) videoSection.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gray-50/50 pb-16">
      <div className="container mx-auto px-4 py-8">

        {/* Header */}
        <CourseHeader
          course={course}
          progress={courseProgress}
          onEnroll={handleEnroll}
          onContinue={() => {
            // Logic to scroll to video or start next lesson
            const videoSection = document.getElementById('video-player-section');
            if (videoSection) videoSection.scrollIntoView({ behavior: 'smooth' });
          }}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            {/* Video Player Section */}
            {selectedVideo ? (
              <div id="video-player-section" className="bg-black rounded-2xl overflow-hidden shadow-2xl ring-1 ring-gray-900/5 aspect-video">
                <CustomVideoPlayer
                  key={selectedVideo.id} // Re-mount on video change
                  src={selectedVideo.video_url}
                  onTimeUpdate={(currentTime, duration) => handleVideoTimeUpdate(selectedVideo.id, currentTime, duration)}
                  onVideoComplete={() => handleMarkComplete(selectedVideo.id)}
                  slideLinks={slideLinks}
                  imageLinks={imageLinks}
                  initialTime={videoProgressMap[selectedVideo.id]?.watched_duration || 0}
                  poster={course.thumbnail}
                  autoPlay={true}
                  submittingTask={!!submittingTask}
                />
                <div className="bg-white p-4 border-t border-gray-100 flex justify-between items-center">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{selectedVideo.title}</h3>
                    <p className="text-sm text-gray-500">
                      {t('course.curriculum.video', { index: selectedVideo.order, title: '' })}
                    </p>
                  </div>
                  {isVideoCompleted(selectedVideo.id) && (
                    <span className="text-green-600 text-sm font-medium flex items-center gap-1">
                      ✓ {t('course.curriculum.completed')}
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-gray-200 rounded-2xl aspect-video flex items-center justify-center text-gray-500">
                {t('course.states.unlockedParams', { title: 'Select a video' })}
              </div>
            )}

            {/* Tabs / Curriculum */}
            <CourseTabs
              course={course}
              curriculumContent={
                <CourseCurriculum
                  chapters={chapters}
                  selectedVideo={selectedVideo}
                  selectedChapter={selectedChapter}
                  chapterVideosMap={chapterVideosMap}
                  videoTasksMap={videoTasksMap}
                  chapterTasksCountMap={chapterTasksCountMap}
                  onVideoSelect={setSelectedVideo}
                  isChapterUnlocked={isChapterUnlocked}
                  isVideoUnlocked={isVideoUnlocked}
                  isVideoCompleted={isVideoCompleted}
                />
              }
            />
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-8 lg:sticky lg:top-24 h-fit">
            <CourseSidebar
              course={course}
              isEnrolled={!!courseProgress}
              progress={courseProgress}
              onEnroll={handleEnroll}
              onContinue={handleContinue}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Export default
export default CourseDetailPage;
